import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, X } from "lucide-react";
import { api } from "@/lib/api";

const POLL_INTERVAL_MS = 3000;

// Shared AudioContext across renders — unlocked on first user interaction
let sharedCtx = null;
let ctxUnlocked = false;
function getCtx() {
  if (sharedCtx) return sharedCtx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  sharedCtx = new Ctx();
  return sharedCtx;
}
function unlockAudio() {
  if (ctxUnlocked) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  // Play a silent buffer to fully unlock
  try {
    const buffer = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    src.start(0);
  } catch (_) {}
  ctxUnlocked = true;
}

/**
 * Polls `/api/hotel/notifications/pending` every 8s for the connected hotel.
 * Each new item triggers a chime + browser Notification + Sonner toast + a
 * floating in-app banner. The backend atomically marks items as seen when
 * returning them, so each notification is shown exactly once.
 */
export default function HotelNotificationListener({ hotelId }) {
  const [banner, setBanner] = useState(null); // { title, message, bookingId }
  const bannerTimer = useRef(null);
  const intervalRef = useRef(null);
  const inflightRef = useRef(false);

  // Ask for OS notification permission once
  useEffect(() => {
    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch (_) {}
    }
  }, []);

  // Unlock WebAudio on the first user interaction anywhere in the app
  useEffect(() => {
    const handler = () => {
      unlockAudio();
      document.removeEventListener("click", handler);
      document.removeEventListener("keydown", handler);
      document.removeEventListener("touchstart", handler);
    };
    document.addEventListener("click", handler, { once: false });
    document.addEventListener("keydown", handler, { once: false });
    document.addEventListener("touchstart", handler, { once: false });
    return () => {
      document.removeEventListener("click", handler);
      document.removeEventListener("keydown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, []);

  useEffect(() => {
    if (!hotelId) return undefined;

    const poll = async () => {
      if (inflightRef.current) return;
      if (document.hidden) return; // don't poll when tab is hidden
      inflightRef.current = true;
      try {
        const { data } = await api.get("/hotel/notifications/pending");
        const items = data?.notifications || [];
        items.forEach((n) => handleNotification(n, setBanner, bannerTimer));
      } catch (_) {
        // silent — avoid noisy errors when user logs out
      } finally {
        inflightRef.current = false;
      }
    };

    // Fire immediately, then schedule
    poll();
    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    // Also poll when tab becomes visible again
    const onVis = () => { if (!document.hidden) poll(); };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      clearInterval(intervalRef.current);
      clearTimeout(bannerTimer.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [hotelId]);

  if (!banner) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[9999] w-[360px] max-w-[90vw] overflow-hidden rounded-2xl border border-primary/20 bg-card shadow-2xl animate-in slide-in-from-right-5 fade-in duration-300"
      data-testid="hotel-push-banner"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
          <BellRing className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-widest text-primary">Notification</p>
          <p className="mt-0.5 font-[Outfit] text-base font-medium leading-tight">{banner.title}</p>
          <p className="mt-1 text-sm text-muted-foreground">{banner.message}</p>
          <div className="mt-3">
            <Link
              to="/dashboard/hotel/history"
              onClick={() => setBanner(null)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              data-testid="push-banner-open"
            >
              Voir la course →
            </Link>
          </div>
        </div>
        <button
          onClick={() => setBanner(null)}
          className="flex-none rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
          aria-label="Fermer"
          data-testid="push-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function handleNotification(n, setBanner, bannerTimer) {
  const title = n.title || "Saor";
  const message = n.message || "";

  // 1) Audio chime — plays a crisp 3-note bell
  try { playChime(); } catch (_) {}

  // 2) In-app floating banner — single visible popup. Sonner toasts and
  //    native browser Notifications are intentionally NOT triggered here:
  //    the same event was previously surfacing as 3 separate popups (toast +
  //    OS notification + banner), which felt spammy. The banner alone keeps
  //    the actionable CTA ("Voir la course") and stays on screen 8 s.
  setBanner({ title, message, bookingId: n.booking_id || null });
  clearTimeout(bannerTimer.current);
  bannerTimer.current = setTimeout(() => setBanner(null), 8000);
}

/**
 * Crystalline 3-note bell using WebAudio. Each note has a fast attack and a
 * long exponential decay, mixed through a light lowpass for warmth.
 */
function playChime() {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});

  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.value = 0.9;
  const filter = ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 6000;
  master.connect(filter).connect(ctx.destination);

  // Pleasant ascending arpeggio: C6 - E6 - G6 (523.25, 659.25, 783.99)
  const notes = [
    { f: 987.77, t: 0.00, d: 0.55 },  // B5
    { f: 1318.51, t: 0.12, d: 0.60 }, // E6
    { f: 1760.00, t: 0.24, d: 0.80 }, // A6 (the "ding")
  ];
  notes.forEach(({ f, t, d }) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = f;
    gain.gain.setValueAtTime(0.0001, now + t);
    gain.gain.exponentialRampToValueAtTime(0.28, now + t + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + t + d);
    osc.connect(gain).connect(master);
    osc.start(now + t);
    osc.stop(now + t + d + 0.05);
  });
}
